/* eslint-disable */
import { Text, HStack, Box, Stack, Button, Spinner } from '@chakra-ui/react';
import { Avatar, CallVideoMinIcon, toaster, VoiceIcon } from '@halo/ui-chat-react';
import { CallStatus, MediaStatus, MsgPayloadType } from '@halo/chat-sdk';
import { MediaStoreInstance } from '@halo/chat-sdk-react';
import { JoinDialog } from './JoinDialog';
import { useStore } from '@halo/zustand-enhance';
import { useTranslation } from 'react-i18next';

const SHOW_COUNT = 10;

interface MediaJoinCallProps {
  disabled?: boolean;
  mediaStoreInstance: MediaStoreInstance;
  mediaJoin: () => Promise<void>;
}

export const MediaJoinCall = ({
  disabled,
  mediaStoreInstance,
  mediaJoin,
}: MediaJoinCallProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [openJoinDialog, setOpenJoinDialog] = useState(false);
  const mediaStore = useStore(mediaStoreInstance);

  const showAccept = useMemo(() => {
    return mediaStore?.status === MediaStatus.JOIN;
  }, [mediaStore?.status]);

  const actionIcon = useMemo(() => {
    return loading ? (
      <Spinner size="sm" color="white" />
    ) : mediaStore?.type === MsgPayloadType.MEDIA_AUDIO_CALL ? (
      <VoiceIcon />
    ) : (
      <CallVideoMinIcon />
    );
  }, [mediaStore?.type, loading]);

  const filterByFirstSegment = useCallback((membersId: string[]) => {
    const seen = new Set();
    const result = [];
    for (const memberId of membersId) {
      const firstPart = memberId.split('.')[0];
      if (!seen.has(firstPart)) {
        seen.add(firstPart);
        result.push(memberId);
      }
    }
    return result;
  }, []);

  const filterMembersId = useMemo(() => {
    return filterByFirstSegment(mediaStore?.membersId || []);
  }, [mediaStore?.membersId]);

  const uniqueCallMembersLength = useMemo(() => filterMembersId?.length ?? 0, [filterMembersId]);

  const handleAccept = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setOpenJoinDialog(false);
    await mediaJoin().catch(() => {
      toaster.error({ title: t('calls.joinFailed') });
    });
    setLoading(false);
  }, [loading, mediaStore]);

  const onHandleJoin = useCallback(async () => {
    setOpenJoinDialog(true);
  }, [loading, mediaStore]);

  useEffect(() => {
    if (showAccept) {
      setLoading(false);
    }
  }, [showAccept]);

  if (
    !mediaStore ||
    (mediaStore?.isSingleSession &&
      (mediaStore?.callStatus === CallStatus.RINGING ||
        mediaStore?.status !== MediaStatus.CREATED))
  ) {
    return null;
  }

  return (
    <>
      <HStack p="3.5" alignItems="center" justifyContent="space-between" w="full">
        <Stack gap="0" pr="1" alignItems="flex-start">
          {!mediaStore?.isSingleSession && (
            <HStack alignItems="center" justifyContent="center" spaceX="-3">
              {filterMembersId?.slice(0, SHOW_COUNT).map((id) => {
                const member = mediaStore?.membersMap[id];
                return (
                  <Avatar
                    src={member?.avatar}
                    key={id}
                    w="24px"
                    h="24px"
                    css={{
                      outlineWidth: '2px',
                      outlineColor: 'bg',
                      outlineStyle: 'solid',
                    }}
                  />
                );
              })}
              {uniqueCallMembersLength > SHOW_COUNT && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  px="1.5"
                  zIndex={10}
                  bg="blackAlpha.70"
                  rounded="14px"
                  fontSize="xs"
                  h="24px"
                  minW="28px"
                  boxSizing="border-box"
                  color="white"
                >
                  +
                  {uniqueCallMembersLength > 99 + SHOW_COUNT
                    ? 99
                    : uniqueCallMembersLength - SHOW_COUNT}
                </Box>
              )}
            </HStack>
          )}
          <Text fontWeight="medium" color="fg.muted" fontSize="sm">
            {mediaStore?.type === MsgPayloadType.MEDIA_AUDIO_CALL && (
              <>
                {mediaStore?.isSingleSession
                  ? t('calls.ongoingVoiceCall')
                  : t('calls.ongoingGroupVoiceCall')}
              </>
            )}
            {mediaStore?.type === MsgPayloadType.MEDIA_VIDEO_CALL && (
              <>
                {mediaStore?.isSingleSession
                  ? t('calls.ongoingVideoCall')
                  : t('calls.ongoingGroupVideoCall')}
              </>
            )}
          </Text>
        </Stack>
        <Button
          bg="#00BC00"
          gap="1"
          rounded="xl"
          disabled={disabled}
          onClick={() => {
            if (mediaStore?.status === MediaStatus.JOIN) {
              mediaStore?.setFullScreen(true);
            } else {
              if (mediaStore?.isOtherDeviceJoinCall) {
                onHandleJoin();
              } else {
                handleAccept();
              }
            }
          }}
        >
          {actionIcon}
          {mediaStore?.status === MediaStatus.JOIN ? t('buttons.return') : t('buttons.join')}
        </Button>
      </HStack>
      <JoinDialog
        open={openJoinDialog}
        onClose={setOpenJoinDialog}
        onOk={handleAccept}
      ></JoinDialog>
    </>
  );
};
